import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from './AuthContext'
import toast from 'react-hot-toast'

const KioskContext = createContext({})

export const useKiosk = () => {
  const context = useContext(KioskContext)
  if (!context) {
    throw new Error('useKiosk must be used within a KioskProvider')
  }
  return context
}

export const KioskProvider = ({ children }) => {
  const { family, user } = useAuth()
  const [isKioskMode, setIsKioskMode] = useState(false)
  const [kioskSettings, setKioskSettings] = useState({
    enabled: false,
    parentPin: '',
    autoLogoutMinutes: 30,
    allowTaskSelfAssignment: true,
    allowRewardRequests: true,
    restrictedNavigation: true,
    showParentAreas: false
  })
  const [kioskSession, setKioskSession] = useState({
    active: false,
    startTime: null,
    lastActivity: null,
    selectedChild: null
  })

  // Load kiosk settings from localStorage
  useEffect(() => {
    if (family?.id) {
      const savedSettings = localStorage.getItem(`kiosk_settings_${family.id}`)
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings)
          setKioskSettings(prev => ({ ...prev, ...settings }))
        } catch (error) {
          console.error('Error loading kiosk settings:', error)
        }
      }
    }
  }, [family?.id])

  // Block browser navigation in kiosk mode
  useEffect(() => {
    if (!isKioskMode) return

    const blockNavigation = (e) => {
      e.preventDefault()
      e.returnValue = 'Kiosk mode is active. Use the exit button to leave.'
      return 'Kiosk mode is active. Use the exit button to leave.'
    }

    const handlePopState = (e) => {
      e.preventDefault()
      // Push the current state back to prevent navigation
      window.history.pushState(null, '', window.location.href)
      toast.error('Navigation blocked in kiosk mode. Use the exit button to leave.')
    }

    const handleKeyDown = (e) => {
      // Block common navigation shortcuts
      if (
        e.key === 'F5' || // Refresh
        (e.ctrlKey && e.key === 'r') || // Ctrl+R refresh
        (e.ctrlKey && e.key === 'R') || // Ctrl+Shift+R hard refresh
        (e.ctrlKey && e.shiftKey && e.key === 'R') ||
        (e.altKey && e.key === 'ArrowLeft') || // Alt+Left (back)
        (e.altKey && e.key === 'ArrowRight') || // Alt+Right (forward)
        (e.ctrlKey && e.key === 'w') || // Ctrl+W close tab
        (e.ctrlKey && e.key === 'W') ||
        (e.ctrlKey && e.key === 't') || // Ctrl+T new tab
        (e.ctrlKey && e.key === 'T') ||
        (e.ctrlKey && e.key === 'n') || // Ctrl+N new window
        (e.ctrlKey && e.key === 'N') ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') || // Dev tools
        e.key === 'F12' // Dev tools
      ) {
        e.preventDefault()
        toast.error('This action is blocked in kiosk mode.')
        return false
      }
    }

    const handleContextMenu = (e) => {
      e.preventDefault()
      toast.error('Right-click is disabled in kiosk mode.')
    }

    // Add event listeners
    window.addEventListener('beforeunload', blockNavigation)
    window.addEventListener('popstate', handlePopState)
    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('contextmenu', handleContextMenu)

    // Push initial state to prevent back navigation
    window.history.pushState(null, '', window.location.href)

    // Override browser navigation methods
    const originalPushState = window.history.pushState
    const originalReplaceState = window.history.replaceState

    window.history.pushState = function(...args) {
      // Allow only kiosk-related navigation
      if (args[2] && args[2].includes('/kiosk')) {
        return originalPushState.apply(this, args)
      }
      toast.error('Navigation is restricted in kiosk mode.')
    }

    window.history.replaceState = function(...args) {
      // Allow only kiosk-related navigation
      if (args[2] && args[2].includes('/kiosk')) {
        return originalReplaceState.apply(this, args)
      }
      toast.error('Navigation is restricted in kiosk mode.')
    }

    return () => {
      window.removeEventListener('beforeunload', blockNavigation)
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('contextmenu', handleContextMenu)
      
      // Restore original methods
      window.history.pushState = originalPushState
      window.history.replaceState = originalReplaceState
    }
  }, [isKioskMode])

  // Auto-logout timer
  useEffect(() => {
    if (!kioskSession.active || !kioskSettings.autoLogoutMinutes) return

    const checkAutoLogout = () => {
      const now = new Date()
      const lastActivity = new Date(kioskSession.lastActivity || kioskSession.startTime)
      const minutesSinceActivity = (now - lastActivity) / (1000 * 60)

      if (minutesSinceActivity >= kioskSettings.autoLogoutMinutes) {
        exitKioskMode()
        toast('Kiosk session ended due to inactivity', { icon: '⏰' })
      }
    }

    const interval = setInterval(checkAutoLogout, 60000) // Check every minute
    return () => clearInterval(interval)
  }, [kioskSession, kioskSettings.autoLogoutMinutes])

  // Check if we're in kiosk mode on page load/refresh
  useEffect(() => {
    const checkKioskState = () => {
      const kioskState = sessionStorage.getItem('kiosk_active')
      const kioskSessionData = sessionStorage.getItem('kiosk_session')
      
      if (kioskState === 'true' && kioskSessionData) {
        try {
          const sessionData = JSON.parse(kioskSessionData)
          setIsKioskMode(true)
          setKioskSession(sessionData)
          
          // Update last activity to current time
          const updatedSession = {
            ...sessionData,
            lastActivity: new Date().toISOString()
          }
          setKioskSession(updatedSession)
          sessionStorage.setItem('kiosk_session', JSON.stringify(updatedSession))
          
          console.log('Restored kiosk mode from session storage')
        } catch (error) {
          console.error('Error restoring kiosk session:', error)
          // Clear invalid session data
          sessionStorage.removeItem('kiosk_active')
          sessionStorage.removeItem('kiosk_session')
        }
      }
    }

    checkKioskState()
  }, [])

  // Persist kiosk state to session storage
  useEffect(() => {
    if (isKioskMode) {
      sessionStorage.setItem('kiosk_active', 'true')
      sessionStorage.setItem('kiosk_session', JSON.stringify(kioskSession))
    } else {
      sessionStorage.removeItem('kiosk_active')
      sessionStorage.removeItem('kiosk_session')
    }
  }, [isKioskMode, kioskSession])

  const updateKioskSettings = (newSettings) => {
    const updatedSettings = { ...kioskSettings, ...newSettings }
    setKioskSettings(updatedSettings)
    
    if (family?.id) {
      localStorage.setItem(`kiosk_settings_${family.id}`, JSON.stringify(updatedSettings))
    }
  }

  const enterKioskMode = (childId = null) => {
    if (!kioskSettings.enabled) {
      toast.error('Kiosk mode is not enabled. Please enable it in settings first.')
      return false
    }

    const now = new Date()
    const newSession = {
      active: true,
      startTime: now.toISOString(),
      lastActivity: now.toISOString(),
      selectedChild: childId
    }

    setKioskSession(newSession)
    setIsKioskMode(true)
    
    // Store in session storage immediately
    sessionStorage.setItem('kiosk_active', 'true')
    sessionStorage.setItem('kiosk_session', JSON.stringify(newSession))
    
    toast.success('Kiosk mode activated! 🎮', { duration: 2000 })
    return true
  }

  const exitKioskMode = () => {
    setKioskSession({
      active: false,
      startTime: null,
      lastActivity: null,
      selectedChild: null
    })
    setIsKioskMode(false)
    
    // Clear session storage
    sessionStorage.removeItem('kiosk_active')
    sessionStorage.removeItem('kiosk_session')
    
    toast.success('Kiosk mode deactivated', { duration: 2000 })
  }

  const validateParentPin = (pin) => {
    return pin === kioskSettings.parentPin
  }

  const updateActivity = () => {
    if (kioskSession.active) {
      const now = new Date().toISOString()
      const updatedSession = {
        ...kioskSession,
        lastActivity: now
      }
      setKioskSession(updatedSession)
      sessionStorage.setItem('kiosk_session', JSON.stringify(updatedSession))
    }
  }

  const switchChild = (childId) => {
    if (kioskSession.active) {
      const updatedSession = {
        ...kioskSession,
        selectedChild: childId,
        lastActivity: new Date().toISOString()
      }
      setKioskSession(updatedSession)
      sessionStorage.setItem('kiosk_session', JSON.stringify(updatedSession))
      updateActivity()
    }
  }

  const value = {
    isKioskMode,
    kioskSettings,
    kioskSession,
    updateKioskSettings,
    enterKioskMode,
    exitKioskMode,
    validateParentPin,
    updateActivity,
    switchChild
  }

  return (
    <KioskContext.Provider value={value}>
      {children}
    </KioskContext.Provider>
  )
}